import { useRef, useState } from 'react'
import { Folder, FolderOpen, Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAdminMediaFolders } from '@/features/admin/hooks/useAdminMediaFolders'
import { useAdminMedia } from '@/features/admin/hooks/useAdminMedia'
import { MediaGrid } from '@/features/admin/components/media/MediaGrid'
import { BrandLoadingBar } from '@/components/ui/brand-loading-bar'
import type { MediaDocument } from '@/features/admin/services/admin.media.service'
import type { MediaFolderDocument } from '@/features/admin/services/admin.mediaFolders.service'

interface MediaPickerProps {
  selectedId: string | null
  onSelect: (item: MediaDocument) => void
  mimeTypeFilter?: string
}

function FolderNav({
  folders,
  selectedFolderId,
  onSelect,
}: {
  folders: MediaFolderDocument[]
  selectedFolderId: string | null
  onSelect: (id: string | null) => void
}) {
  function renderFolder(folder: MediaFolderDocument, depth = 0): React.ReactNode {
    const isSelected = selectedFolderId === folder.id
    const children = folders.filter((f) => f.parentId === folder.id)

    return (
      <div key={folder.id}>
        <button
          className={cn(
            'w-full flex items-center gap-1.5 rounded py-1 pr-2 text-sm transition-colors text-left',
            isSelected ? 'bg-accent font-medium' : 'hover:bg-muted'
          )}
          style={{ paddingLeft: `${(depth + 1) * 12}px` }}
          onClick={() => onSelect(folder.id)}
        >
          {isSelected ? (
            <FolderOpen className="h-3.5 w-3.5 shrink-0 text-brand-orange" />
          ) : (
            <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate">{folder.name}</span>
        </button>
        {children.map((child) => renderFolder(child, depth + 1))}
      </div>
    )
  }

  const rootFolders = folders.filter((f) => f.parentId === null)

  return (
    <div className="flex flex-col gap-0.5 py-1">
      <button
        className={cn(
          'flex items-center gap-1.5 rounded px-2 py-1.5 text-sm font-medium transition-colors text-left',
          selectedFolderId === null ? 'bg-accent' : 'hover:bg-muted'
        )}
        onClick={() => onSelect(null)}
      >
        {selectedFolderId === null ? (
          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-brand-orange" />
        ) : (
          <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span>All Media</span>
      </button>
      {rootFolders.map((f) => renderFolder(f))}
    </div>
  )
}

export function MediaPicker({ selectedId, onSelect, mimeTypeFilter }: MediaPickerProps) {
  const [folderId, setFolderId] = useState<string | null>(null)
  const { folders } = useAdminMediaFolders()
  const { items, loading, uploadProgress, uploadFiles } = useAdminMedia({ folderId })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const readyItems = items.filter(
    (item) =>
      item.status === 'ready' &&
      (!mimeTypeFilter || item.mimeType.startsWith(mimeTypeFilter))
  )

  const isUploading = uploadProgress !== null
  const canUpload = folderId !== null

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (files.length > 0) uploadFiles(files)
    e.target.value = ''
  }

  return (
    <div className="flex h-full min-h-0 gap-3">
      {/* Folder tree */}
      <div className="w-36 shrink-0 overflow-y-auto rounded-lg border bg-muted/30 py-1">
        <FolderNav
          folders={folders}
          selectedFolderId={folderId}
          onSelect={setFolderId}
        />
      </div>

      {/* Media grid + upload */}
      <div className="flex flex-1 flex-col min-h-0 gap-2">
        {/* Upload bar */}
        <div className="shrink-0">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          {isUploading ? (
            <div className="rounded-md border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground overflow-hidden">
              <div className="flex items-center justify-between mb-1">
                <span>Uploading {uploadProgress.completed}/{uploadProgress.total}…</span>
                <span>{Math.round(uploadProgress.overallPct)}%</span>
              </div>
              <BrandLoadingBar height="sm" value={uploadProgress.overallPct} className="rounded-full" />
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={!canUpload}
              className={cn(
                'flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed py-1.5 text-xs transition-colors',
                canUpload
                  ? 'text-muted-foreground hover:border-primary hover:text-primary cursor-pointer'
                  : 'text-muted-foreground/40 cursor-not-allowed'
              )}
            >
              <Upload className="h-3.5 w-3.5" />
              {canUpload ? 'Upload to this folder' : 'Select a folder to upload'}
            </button>
          )}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-1">
          <MediaGrid
            items={readyItems}
            loading={loading}
            selectedId={selectedId}
            onSelect={onSelect}
            compact
          />
        </div>
      </div>
    </div>
  )
}
